"use client";

import Image from "next/image";
import Link from "next/link";
import { usePathname } from "next/navigation";
import { useEffect, useMemo } from "react";

import { PRIMARY_NAVIGATION } from "@/content/site";
import { useNavigation } from "@/components/navigation-context";

export function Header() {
  const pathname = usePathname();
  const { isMobileNavOpen, toggleMobileNav, closeMobileNav } = useNavigation();

  useEffect(() => {
    closeMobileNav();
  }, [pathname, closeMobileNav]);

  return (
    <header className="sticky top-0 z-50 border-b border-[rgba(6,55,102,0.08)] bg-white/80 backdrop-blur">
      <div className="container flex items-center justify-between py-5">
        <Link href="/" className="flex items-center">
          <Image
            src="/logo.png"
            alt="Regnova logo"
            width={160}
            height={48}
            priority
          />
        </Link>

        <nav className="hidden items-center gap-8 text-sm font-medium text-muted lg:flex">
          <NavLinks pathname={pathname} />
        </nav>

        <div className="hidden items-center gap-4 lg:flex">
          <LoginPill />
        </div>

        <button
          type="button"
          onClick={toggleMobileNav}
          className="inline-flex h-10 w-10 items-center justify-center rounded-full border border-[rgba(6,55,102,0.18)] text-[#063766] transition hover:border-[#2380dd] hover:text-[#2380dd] lg:hidden"
          aria-label="Toggle navigation"
          aria-expanded={isMobileNavOpen}
        >
          <span className="sr-only">Toggle navigation</span>
          <MenuIcon isOpen={isMobileNavOpen} />
        </button>
      </div>

      <MobileNavigation />
    </header>
  );
}

function MobileNavigation() {
  const { isMobileNavOpen, closeMobileNav } = useNavigation();
  const pathname = usePathname();

  useEffect(() => {
    closeMobileNav();
  }, [pathname, closeMobileNav]);

  if (!isMobileNavOpen) {
    return null;
  }

  return (
    <div className="border-t border-[rgba(6,55,102,0.08)] bg-white/95 backdrop-blur lg:hidden">
      <div className="container flex flex-col gap-4 py-6">
        <NavLinks pathname={pathname} mobile />
        <LoginPill />
      </div>
    </div>
  );
}

function LoginPill() {
  return (
    <Link
      href="https://portal.regnovagh.com"
      target="_blank"
      rel="noopener noreferrer"
      className="group relative inline-flex items-center gap-2 rounded-full bg-[#063766] px-4 py-2 text-sm font-semibold text-white shadow-soft transition hover:bg-[#052d55]"
    >
      <span className="h-2 w-2 rounded-full bg-[#1bb6b9]" aria-hidden />
      <span>Login</span>
      <span className="pointer-events-none absolute -bottom-8 left-1/2 hidden -translate-x-1/2 whitespace-nowrap rounded-full bg-[#1c4e86] px-4 py-1 text-xs text-white shadow-soft group-hover:block">
        PMS portal
      </span>
    </Link>
  );
}

type MenuIconProps = {
  isOpen: boolean;
};

function MenuIcon({ isOpen }: MenuIconProps) {
  return (
    <svg
      xmlns="http://www.w3.org/2000/svg"
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="1.8"
      className="h-6 w-6"
    >
      {isOpen ? (
        <path strokeLinecap="round" strokeLinejoin="round" d="M6 6l12 12M6 18L18 6" />
      ) : (
        <path strokeLinecap="round" strokeLinejoin="round" d="M4 7h16M4 12h16M4 17h16" />
      )}
    </svg>
  );
}

function NavLinks({ pathname, mobile = false }: { pathname: string; mobile?: boolean }) {
  const items = useMemo(() => PRIMARY_NAVIGATION, []);

  return (
    <>
      {items.map((item) => {
        const isActive = pathname.startsWith(item.href);
        const baseClass = mobile
          ? "text-base font-medium text-muted transition hover:text-[#2380dd]"
          : "transition hover:text-[#2380dd]";
        const activeClass = isActive ? " text-[#2380dd]" : mobile ? "" : " text-muted";

        return (
          <Link key={item.href} href={item.href} className={`${baseClass}${activeClass}`}>
            {item.label}
          </Link>
        );
      })}
    </>
  );
}

